import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPin, Plus, Trash2 } from 'lucide-react'
import { usePlaces, useAddPlace, useDeletePlace, useUpdatePlace } from './usePlaces'
import { usePoints, useAddPoint, useDeletePoint, useUpdatePoint } from '../timeline/usePoints'
import { POINT_TYPE_OPTIONS } from '../../shared/lib/pointMeta'
import { tripDayCount, addDays } from '../../shared/lib/dates'
import { useTrip } from './useTrip'
import { Button } from '../../shared/components/Button'
import type { PointType, TripPlace } from '../../shared/api/types'
import { searchPlaces, type PlaceSearchResult } from '../../shared/api/placeSearch'

export function Places() {
  const { id = '' } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const trip = useTrip(id)
  const places = usePlaces(id)
  const visits = usePoints(id)
  const addPlace = useAddPlace(id)
  const deletePlace = useDeletePlace(id)
  const addVisit = useAddPoint(id)
  const deleteVisit = useDeletePoint(id)
  const updateVisit = useUpdatePoint(id)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<PointType>('free')
  const [address, setAddress] = useState('')
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [providerId, setProviderId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [timing, setTiming] = useState<'any' | 'window' | 'day' | 'exact' | 'stay'>('any')
  const [day, setDay] = useState(0)
  const [time, setTime] = useState('')
  const [endDay, setEndDay] = useState(0)
  const [duration, setDuration] = useState(60)
  const [priority, setPriority] = useState<'must' | 'optional'>('must')
  const [portWindowId, setPortWindowId] = useState('')
  const [error, setError] = useState('')
  const count = tripDayCount(trip.data?.start_date ?? null, trip.data?.end_date ?? null)

  async function submitPlace(e: FormEvent) {
    e.preventDefault(); setError('')
    try {
      await addPlace.mutateAsync({ title: title.trim(), type, address: address.trim(), provider_id: providerId,
        lat: lat === '' ? null : Number(lat), lng: lng === '' ? null : Number(lng) })
      setTitle(''); setAddress(''); setLat(''); setLng(''); setProviderId(null)
    } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
  }

  async function doSearch() {
    setError(''); setSearching(true)
    try { setResults(await searchPlaces(query, i18n.language)) }
    catch (err) { setError(err instanceof Error ? err.message : String(err)) }
    finally { setSearching(false) }
  }

  async function schedule(e: FormEvent, place: TripPlace) {
    e.preventDefault(); setError('')
    if (timing === 'exact' && !time) { setError(t('places.time_required')); return }
    const start = trip.data?.start_date
    try {
      await addVisit.mutateAsync({
        place_id: place.id, title: place.title, type: place.type,
        address: place.address, lat: place.lat, lng: place.lng,
        day_index: timing === 'any' || timing === 'window' ? -1 : day,
        port_window_id: portWindowId || null,
        timing, priority, duration_minutes: Math.max(0, duration),
        date: timing === 'exact' && start ? addDays(start, day) : null,
        time: timing === 'exact' ? time : null,
        window_start: (timing === 'window' || timing === 'stay') && start ? addDays(start, day) : null,
        window_end: (timing === 'window' || timing === 'stay') && start ? addDays(start, endDay) : null,
      })
      setSelected(null); setTiming('any'); setTime(''); setDuration(60)
      setPortWindowId('')
    } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
  }

  return <div className="mx-auto max-w-4xl space-y-6" dir={document.documentElement.dir || 'ltr'}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="font-display text-2xl font-bold">{t('places.title')}</h1>
        <p className="text-sm text-st-muted">{t('places.intro')}</p></div>
      <Link to={`/trips/${id}/timeline`}><Button variant="outline">{t('places.open_calendar')}</Button></Link>
    </div>
    <form onSubmit={submitPlace} className="rounded-st-lg border border-st-line bg-st-surface p-4">
      <h2 className="mb-3 font-semibold">{t('places.add')}</h2>
      <div className="mb-3 flex gap-2"><input className="input min-w-0 flex-1" value={query} onChange={e=>setQuery(e.target.value)}
        placeholder={t('places.search')} aria-label={t('places.search')} />
        <Button type="button" variant="outline" disabled={searching||query.trim().length<2} onClick={()=>void doSearch()}>{t('places.search_button')}</Button></div>
      {results.length>0&&<ul className="mb-3 max-h-48 overflow-y-auto rounded border border-st-line">{results.map((r,i)=><li key={`${r.provider_id}-${i}`}>
        <button type="button" className="w-full p-2 text-start text-sm hover:bg-st-surface-2" onClick={()=>{
          setTitle(r.title);setAddress(r.address);setLat(String(r.lat));setLng(String(r.lng));setProviderId(r.provider_id);setResults([])
        }}>{r.title} · {r.address}</button></li>)}</ul>}
      <div className="grid gap-3 sm:grid-cols-2">
        <input className="input" required aria-label={t('places.name')} placeholder={t('places.name')}
          value={title} onChange={e => setTitle(e.target.value)} />
        <select className="input" aria-label={t('places.type')} value={type}
          onChange={e => setType(e.target.value as PointType)}>{POINT_TYPE_OPTIONS.map(o =>
            <option key={o.value} value={o.value}>{t(`point_types.${o.value}`)}</option>)}</select>
        <input className="input" aria-label={t('places.address')} placeholder={t('places.address')}
          value={address} onChange={e => setAddress(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <input className="input" type="number" step="any" min="-90" max="90" aria-label={t('places.latitude')}
            placeholder={t('places.latitude')} value={lat} onChange={e => setLat(e.target.value)} />
          <input className="input" type="number" step="any" min="-180" max="180" aria-label={t('places.longitude')}
            placeholder={t('places.longitude')} value={lng} onChange={e => setLng(e.target.value)} />
        </div>
      </div>
      <div className="mt-3"><Button type="submit" variant="accent" disabled={addPlace.isPending}><Plus className="h-4 w-4" /> {t('places.add')}</Button></div>
    </form>
    {error && <p role="alert" className="text-sm text-st-danger">{error}</p>}
    {places.isLoading && <p>{t('places.loading')}</p>}
    <div className="grid gap-3 sm:grid-cols-2">
      {places.data?.map(place => {
        const placeVisits = (visits.data ?? []).filter(p => (p.place_id ?? p.id) === place.id)
        return <section key={place.id} className="rounded-st-lg border border-st-line bg-st-surface p-4">
          <div className="flex items-start gap-2">
            <MapPin className="mt-1 h-4 w-4 shrink-0 text-st-primary" />
            <div className="min-w-0 flex-1"><h2 className="font-semibold">{place.title}</h2>
              <p className="text-sm text-st-muted">{place.address || place.type}</p></div>
            <button type="button" aria-label={t('places.delete')} onClick={() => void deletePlace.mutateAsync(place.id)}
              className="p-2 text-st-muted hover:text-st-danger"><Trash2 className="h-4 w-4" /></button>
          </div>
          {placeVisits.length === 0 && <p className="mt-2 text-xs text-st-muted">{t('places.unscheduled')}</p>}
          <ul className="mt-2 space-y-2">{placeVisits.map(v => <li key={v.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="min-w-0 flex-1">{v.day_index < 0 ? t('places.unscheduled') : `${t('places.day')} ${v.day_index + 1}`} {v.time ?? ''}
              {v.legacy_date_conflict && <span className="ms-2 rounded bg-st-warning/15 px-1 text-st-warning">{t('places.legacy_conflict')}</span>}</span>
            {v.timing !== 'stay' && count > 0 && <><select className="input" aria-label={`${place.title}: ${t('places.day')}`}
              value={v.day_index} onChange={e => { const next = Number(e.target.value); const date = next >= 0 && trip.data?.start_date ? addDays(trip.data.start_date, next) : null
                void updateVisit.mutateAsync({pointId:v.id,patch:{day_index:next,date,time:null,timing:next<0?'any':'day'}})
                  .catch(err=>setError(err instanceof Error?err.message:String(err))) }}>
              <option value={-1}>{t('places.unscheduled')}</option>
              {Array.from({length:count},(_,i)=><option key={i} value={i}>{t('places.day')} {i+1}</option>)}
            </select><input className="input w-28" type="time" aria-label={`${place.title}: ${t('places.time')}`}
              value={v.time ?? ''} onChange={e => { const time = e.target.value; const next = v.day_index >= 0 ? v.day_index : 0
                const date = trip.data?.start_date ? addDays(trip.data.start_date,next) : null
                void updateVisit.mutateAsync({pointId:v.id,patch:{day_index:next,date,time:time || null,timing:time?'exact':'day'}})
                  .catch(err=>setError(err instanceof Error?err.message:String(err))) }} /></>}
            <Link className="text-st-primary underline" to={`/trips/${id}/points/${v.id}`}>{t('places.details')}</Link>
            <button type="button" aria-label={t('places.delete_visit')} onClick={() => void deleteVisit.mutateAsync(v.id)}
              className="text-st-muted hover:text-st-danger"><Trash2 className="h-4 w-4" /></button>
          </li>)}</ul>
          <OpeningHoursEditor tripId={id} place={place} min={trip.data?.start_date ?? undefined} max={trip.data?.end_date ?? undefined} />
          {selected === place.id ? <form onSubmit={e => void schedule(e, place)} className="mt-3 space-y-2 border-t border-st-line pt-3">
            <select className="input w-full" aria-label={t('places.timing')} value={timing}
              onChange={e => setTiming(e.target.value as typeof timing)}>
              {(['any','window','day','exact','stay'] as const).map(x => <option key={x} value={x}>{t(`places.timing_${x}`)}</option>)}
            </select>
            {timing !== 'any' && <div className="flex flex-wrap gap-2">
              <label className="text-xs">{t('places.day')} <select className="input" value={day} onChange={e => setDay(Number(e.target.value))}>
                {Array.from({length:count},(_,i)=><option key={i} value={i}>{trip.data?.start_date ? addDays(trip.data.start_date,i) : `Day ${i+1}`}</option>)}</select></label>
              {(timing === 'window' || timing === 'stay') && <label className="text-xs">{t('places.until')} <select className="input" value={endDay} onChange={e => setEndDay(Number(e.target.value))}>
                {Array.from({length:count},(_,i)=><option key={i} value={i}>{trip.data?.start_date ? addDays(trip.data.start_date,i) : `Day ${i+1}`}</option>)}</select></label>}
              {timing === 'exact' && <label className="text-xs">{t('places.time')} <input className="input" type="time" value={time} onChange={e => setTime(e.target.value)} /></label>}
            </div>}
            <div className="flex flex-wrap gap-2">
              <label className="text-xs">{t('places.duration')} <input className="input w-20" type="number" min="0" value={duration} onChange={e => setDuration(Number(e.target.value))} /></label>
              <label className="text-xs">{t('places.priority')} <select className="input" value={priority} onChange={e => setPriority(e.target.value as typeof priority)}>
                <option value="must">{t('places.must')}</option><option value="optional">{t('places.optional')}</option></select></label>
            </div>
            {(visits.data ?? []).some(v=>v.type==='port') && <label className="block text-xs">{t('places.port_window')}
              <select className="input ms-2" value={portWindowId} onChange={e=>setPortWindowId(e.target.value)}>
                <option value="">{t('places.no_port_window')}</option>
                {(visits.data ?? []).filter(v=>v.type==='port').map(v=><option key={v.id} value={v.id}>{v.title} · {v.date ?? ''}</option>)}
              </select></label>}
            <div className="flex gap-2"><Button type="submit" variant="accent" disabled={addVisit.isPending}>{t('places.save_visit')}</Button>
              <Button type="button" variant="ghost" onClick={() => setSelected(null)}>{t('places.cancel')}</Button></div>
          </form> : <button type="button" onClick={() => setSelected(place.id)} className="mt-3 text-sm font-medium text-st-primary">+ {t('places.add_visit')}</button>}
        </section>
      })}
    </div>
    <style>{`.input{min-height:2.6rem;border:1px solid var(--color-st-line);border-radius:var(--radius-st-sm);background:var(--color-st-surface);padding:.55rem .7rem;color:var(--color-st-text);min-width:0}`}</style>
  </div>
}

function OpeningHoursEditor({tripId,place,min,max}:{tripId:string;place:TripPlace;min?:string;max?:string}) {
  const {t}=useTranslation()
  const update=useUpdatePlace(tripId)
  const [date,setDate]=useState('')
  const [open,setOpen]=useState('09:00')
  const [close,setClose]=useState('18:00')
  const [closed,setClosed]=useState(false)
  const [error,setError]=useState('')
  const entries=Object.entries(place.opening_hours ?? {}).sort(([a],[b])=>a.localeCompare(b))
  async function save(e:FormEvent){e.preventDefault();setError('')
    if(!date||(!closed&&close<=open)){setError(t('places.hours_invalid'));return}
    try{await update.mutateAsync({id:place.id,patch:{opening_hours:{...place.opening_hours,[date]:closed?[]:[[open,close]]}}})
      setDate('')}
    catch(err){setError(err instanceof Error?err.message:String(err))}}
  return <details className="mt-3 border-t border-st-line pt-2 text-sm"><summary className="cursor-pointer text-st-primary">{t('places.hours')}</summary>
    <p className="mt-1 text-xs text-st-muted">{t('places.hours_hint')}</p>
    <ul className="mt-2 space-y-1">{entries.map(([day,windows])=><li key={day} className="flex items-center gap-2">
      <span className="flex-1">{day}: {windows.length?windows.map(w=>w.join('–')).join(', '):t('places.closed')}</span>
      <button type="button" className="text-st-danger" onClick={()=>void update.mutateAsync({id:place.id,
        patch:{opening_hours:Object.fromEntries(entries.filter(([d])=>d!==day))}})}>{t('places.remove_hours')}</button></li>)}</ul>
    <form onSubmit={e=>void save(e)} className="mt-2 flex flex-wrap items-end gap-2">
      <label>{t('places.date')}<input type="date" className="input" min={min} max={max} value={date} onChange={e=>setDate(e.target.value)} /></label>
      {!closed&&<><label>{t('places.opens')}<input type="time" className="input" value={open} onChange={e=>setOpen(e.target.value)} /></label>
        <label>{t('places.closes')}<input type="time" className="input" value={close} onChange={e=>setClose(e.target.value)} /></label></>}
      <label className="pb-2"><input type="checkbox" checked={closed} onChange={e=>setClosed(e.target.checked)} /> {t('places.closed')}</label>
      <Button type="submit" variant="outline" disabled={update.isPending}>{t('places.save_hours')}</Button>
    </form>
    {error&&<p role="alert" className="text-st-danger">{error}</p>}
  </details>
}
